/**
 * Public cache-version client.
 *
 * Backend exposes per-resource monotonic counters at `GET /api/cache/version`
 * that bump on every admin write to a tracked resource (articles, lender
 * rates, etc.). The frontend subscribes to a single resource per usage so
 * an open article tab doesn't refetch when an unrelated lender rate is
 * edited, and vice-versa.
 *
 * Usage:
 *   useRevalidateOnCacheBump("articles", () => refetchArticle());
 *   useRevalidateOnCacheBump("lender_rates", () => refetchRates());
 *
 * The callback fires whenever the server-side counter for the chosen
 * resource is observed to have advanced past the value seen on mount. We
 * deliberately do NOT fire on first load (would cause every page to
 * double-fetch on mount).
 *
 * Backwards-compatible: callers that pass only the callback (no resource)
 * subscribe to "any resource bump" via the aggregate `version` field —
 * matches iter-46 behaviour exactly.
 */
import { useEffect, useRef } from "react";
import { api } from "./api";

const DEFAULT_POLL_MS = 60_000;

let inFlight = null;
async function fetchSnapshot() {
  if (inFlight) return inFlight;
  inFlight = api
    .get("/cache/version")
    .then((r) => r.data || null)
    .catch(() => null)
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/**
 * Subscribe to cache-version bumps.
 *
 * Signatures:
 *   useRevalidateOnCacheBump(resource, onBump, options?)   ← preferred
 *   useRevalidateOnCacheBump(onBump, options?)             ← legacy/global
 */
export function useRevalidateOnCacheBump(...args) {
  let resource = null;
  let onBump;
  let options = {};
  if (typeof args[0] === "string") {
    [resource, onBump, options = {}] = args;
  } else {
    [onBump, options = {}] = args;
  }
  const intervalMs = options.intervalMs ?? DEFAULT_POLL_MS;

  const baselineRef = useRef(null);
  const onBumpRef = useRef(onBump);
  useEffect(() => {
    onBumpRef.current = onBump;
  }, [onBump]);

  useEffect(() => {
    let cancelled = false;
    const observedValue = (snapshot) => {
      if (!snapshot) return null;
      // Per-resource subscription
      if (resource && snapshot.versions && snapshot.versions[resource] != null) {
        return snapshot.versions[resource];
      }
      // Aggregate (legacy callers): use the global max
      return snapshot.version ?? 0;
    };
    const tick = async () => {
      const snapshot = await fetchSnapshot();
      if (cancelled) return;
      const v = observedValue(snapshot);
      if (v === null) return;
      if (baselineRef.current === null) {
        baselineRef.current = v;
        return;
      }
      if (v > baselineRef.current) {
        baselineRef.current = v;
        onBumpRef.current?.();
      }
    };
    tick(); // seed the baseline
    const id = setInterval(tick, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [resource, intervalMs]);
}
